import i18n from '@/i18n';
import { fixedUi } from '@/i18n/fixedUi';
import { localizedSiteLabel } from '@/i18n/catalogDisplayText';
/** TikTok 模板库归属：单店铺独享 / 团队店铺共享 / 官方（商品 / 属性 / 尺码 / 实拍图 / 证书共用）。
 * 单店铺独享 = 当前店铺可见；团队店铺共享 = 团队下同类型店铺相同类目可见（与顶栏个人/团队工作区无关）。
 */
export const TIKTOK_TEMPLATE_SCOPES = {
  personal: 'PERSONAL',
  team: 'TEAM',
  official: 'OFFICIAL',
};

export const TIKTOK_TEMPLATE_SCOPE_LABELS = {
  [TIKTOK_TEMPLATE_SCOPES.personal]: '单店铺独享',
  [TIKTOK_TEMPLATE_SCOPES.team]: '团队店铺共享',
  [TIKTOK_TEMPLATE_SCOPES.official]: '官方',
};

export const TIKTOK_TEMPLATE_SCOPE_TABS = [
  { id: TIKTOK_TEMPLATE_SCOPES.personal, label: TIKTOK_TEMPLATE_SCOPE_LABELS.PERSONAL },
  { id: TIKTOK_TEMPLATE_SCOPES.team, label: TIKTOK_TEMPLATE_SCOPE_LABELS.TEAM },
  { id: TIKTOK_TEMPLATE_SCOPES.official, label: TIKTOK_TEMPLATE_SCOPE_LABELS.OFFICIAL },
];

/** 刊登选模板：有团队时仅单店铺独享 / 团队店铺共享 */
export const TIKTOK_LISTING_TEMPLATE_SCOPE_TABS = [
  { id: TIKTOK_TEMPLATE_SCOPES.personal, label: TIKTOK_TEMPLATE_SCOPE_LABELS.PERSONAL },
  { id: TIKTOK_TEMPLATE_SCOPES.team, label: TIKTOK_TEMPLATE_SCOPE_LABELS.TEAM },
];

/** 可见 Tab：单店铺独享 + 官方始终；团队店铺共享需团队权限 */
export function resolveVisibleTiktokTemplateScopes(canAccessTeam = false) {
  return TIKTOK_TEMPLATE_SCOPE_TABS.filter(
    (tab) => tab.id !== TIKTOK_TEMPLATE_SCOPES.team || Boolean(canAccessTeam),
  );
}

export function isOfficialTemplateScope(scope) {
  return String(scope || '').toUpperCase() === TIKTOK_TEMPLATE_SCOPES.official;
}

export function isTeamTemplateScope(scope) {
  return String(scope || '').toUpperCase() === TIKTOK_TEMPLATE_SCOPES.team;
}

export function isPersonalTemplateScope(scope) {
  return String(scope || '').toUpperCase() === TIKTOK_TEMPLATE_SCOPES.personal;
}

export function resolveTemplateScopeLabel(scope) {
  const key = String(scope || '').toUpperCase();
  const raw = TIKTOK_TEMPLATE_SCOPE_LABELS[key] || key || '';
  return raw ? fixedUi(raw) : '';
}

export function resolveTemplateRowShopId(row, fallbackShopId) {
  const id = row?.shopId;
  if (id != null && String(id).trim() !== '') {
    return String(id);
  }
  return fallbackShopId ? String(fallbackShopId) : '';
}

export function rowCanEditScopedTemplate(row) {
  if (!row) return false;
  if (row.canEdit === false) return false;
  if (row.canEdit === true) return true;
  return row.isOwner === true;
}

export function rowCanDeleteScopedTemplate(row) {
  if (!row) return false;
  if (row.canDelete === false) return false;
  if (row.canDelete === true) return true;
  return row.isOwner === true;
}

export function rowCanPublishScopedTemplate(row) {
  if (!row) return false;
  if (row.canPublish === false) return false;
  if (row.canPublish === true) return true;
  return row.isOwner === true;
}

export function rowCanCopyScopedTemplate(row) {
  if (!row) return false;
  if (row.canCopy === false) return false;
  return true;
}

/**
 * 复制菜单：官方/团队店铺共享 → 单店铺独享+团队店铺共享；单店铺独享 Tab → 两者均可。
 * 无团队权限时仅「复制到单店铺独享」。
 */
export function buildTemplateCopyMenuOptions({
  scopeTab,
  isOfficialTab,
  onCopy,
  canAccessTeam = false,
} = {}) {
  const toPersonal = () => onCopy?.(TIKTOK_TEMPLATE_SCOPES.personal);
  const toTeam = () => onCopy?.(TIKTOK_TEMPLATE_SCOPES.team);
  if (!canAccessTeam) {
    return [{ key: 'personal', label: i18n.t('productTemplate.copyToPersonal', { ns: 'workbench' }), onClick: toPersonal }];
  }
  if (isOfficialTab || isTeamTemplateScope(scopeTab)) {
    return [
      { key: 'personal', label: i18n.t('productTemplate.copyToPersonal', { ns: 'workbench' }), onClick: toPersonal },
      { key: 'team', label: i18n.t('productTemplate.copyToTeam', { ns: 'workbench' }), onClick: toTeam },
    ];
  }
  return [
    { key: 'personal', label: i18n.t('productTemplate.copyToPersonal', { ns: 'workbench' }), onClick: () => onCopy?.(scopeTab) },
    { key: 'team', label: i18n.t('productTemplate.copyToTeam', { ns: 'workbench' }), onClick: toTeam },
  ];
}

export function templateCopiedToast(targetScope) {
  return isTeamTemplateScope(targetScope)
    ? i18n.t('productTemplate.copiedTeam', { ns: 'workbench' })
    : i18n.t('productTemplate.copiedPersonal', { ns: 'workbench' });
}

/**
 * 批量移动菜单：单店铺独享 Tab → 移动到团队店铺共享；团队店铺共享 Tab → 移动到单店铺独享。
 * 官方 Tab 或没有团队权限时无可移动目标，返回空。
 */
export function buildBatchMoveMenuOptions({
  scopeTab,
  isOfficialTab,
  onMove,
  canAccessTeam = false,
} = {}) {
  if (isOfficialTab || !canAccessTeam) return [];
  if (isTeamTemplateScope(scopeTab)) {
    return [{
      key: 'personal',
      label: i18n.t('productTemplate.moveToPersonal', { ns: 'workbench' }),
      onClick: () => onMove?.(TIKTOK_TEMPLATE_SCOPES.personal),
    }];
  }
  return [{
    key: 'team',
    label: i18n.t('productTemplate.moveToTeam', { ns: 'workbench' }),
    onClick: () => onMove?.(TIKTOK_TEMPLATE_SCOPES.team),
  }];
}

/**
 * 工作区模板库「批量操作」聚合菜单（对齐官方 Admin）。
 * - 复制 → 二级
 * - 移动 → 二级（官方 Tab 无）
 * - 移动站点 → 二级（仅产品模板传入 sites）
 * - 批量删除（官方 Tab 无）
 */
export function buildTiktokWorkspaceBatchOpsMenu({
  scopeTab,
  isOfficialTab = false,
  canAccessTeam = false,
  selectedCount = 0,
  onCopy,
  onMove,
  onDelete,
  deleteDisabled = false,
  sites = null,
  onMoveSites,
  onPickMultiSites,
  moveSitesDisabled = false,
} = {}) {
  const copyMenu = buildTemplateCopyMenuOptions({
    scopeTab,
    isOfficialTab,
    canAccessTeam,
    onCopy,
  });
  const moveMenu = buildBatchMoveMenuOptions({
    scopeTab,
    isOfficialTab,
    canAccessTeam,
    onMove,
  });

  const menu = [
    {
      key: 'copy',
      label: i18n.t('productTemplate.copy', { ns: 'workbench' }),
      disabled: selectedCount <= 0 || !copyMenu.length,
      menu: copyMenu,
    },
  ];

  if (moveMenu.length) {
    menu.push({
      key: 'move',
      label: i18n.t('productTemplate.move', { ns: 'workbench' }),
      disabled: selectedCount <= 0,
      menu: moveMenu,
    });
  }

  if (Array.isArray(sites)) {
    const siteItems = sites
      .filter((row) => row?.siteAbbr)
      .map((row) => {
        const abbr = String(row.siteAbbr);
        const label = row.siteName && row.siteName !== '—'
          ? row.siteName
          : abbr;
        return {
          key: `move-site-${abbr}`,
          label,
          onClick: () => onMoveSites?.([abbr]),
        };
      });
    menu.push({
      key: 'move-sites',
      label: selectedCount > 0 ? i18n.t('productTemplate.moveSitesCount', { ns: 'workbench', count: selectedCount }) : i18n.t('productTemplate.moveSites', { ns: 'workbench' }),
      disabled: Boolean(moveSitesDisabled) || selectedCount <= 0 || (siteItems.length === 0 && !onPickMultiSites),
      menu: [
        ...siteItems,
        {
          key: 'move-sites-multi',
          label: i18n.t('productTemplate.pickMultiSites', { ns: 'workbench' }),
          disabled: siteItems.length === 0,
          onClick: onPickMultiSites,
        },
      ],
    });
  }

  if (!isOfficialTab) {
    menu.push({
      key: 'delete',
      label: selectedCount > 0 ? i18n.t('productTemplate.batchDeleteCount', { ns: 'workbench', count: selectedCount }) : i18n.t('returnList.batchDelete', { ns: 'workbench' }),
      tone: 'danger',
      disabled: Boolean(deleteDisabled) || selectedCount <= 0,
      onClick: onDelete,
    });
  }

  return menu;
}

export function resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab } = {}) {
  return targetScope || (isOfficialTab ? TIKTOK_TEMPLATE_SCOPES.personal : scopeTab);
}

/**
 * 复制源归属：与批量复制一致。
 * 官方 Tab 强制 OFFICIAL，避免 row.scope 异常时被当成店铺模板复制（创建人会写成当前用户）。
 */
export function resolveCopySourceScope(row, { isOfficialTab, scopeTab } = {}) {
  if (isOfficialTab) return TIKTOK_TEMPLATE_SCOPES.official;
  return row?.scope || scopeTab;
}
